import React, {
  MutableRefObject,
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import { VStack, Columns, Column, Box, Flex } from "@sqymagma/elements";
import { useViewportSpy } from "beautiful-react-hooks";

import { useApp } from "@contexts";
import { remoteApi } from "@services";
import { MoodState, MoodStats, SelectOption } from "@types";
import { Button, Loader, Select } from "@elements";
import { Modal, MoodWidget } from "@components";

import Timeline from "./Timeline";
import AverageChart from "./AverageChart";
import DayChart from "./DayChart";
import AverageCard from "./AverageCard";

const itemsPerPage = 10; // number of moods to retrieve with lazy load

const Mood = () => {
  const { savedMoods } = useApp();
  const [moods, setMoods] = useState<Array<MoodState>>();
  const [stats, setStats] = useState<MoodStats>();
  const timelineRef = useRef<HTMLDivElement>(null);
  const [newMoods, setNewMoods] = useState(0);

  const savedOption = useMemo(
    () => ({
      label: `Mensajes guardados (${savedMoods.length})`,
      value: "saved",
    }),
    [savedMoods.length]
  );
  const filterOptions: Array<SelectOption> = useMemo(
    () => [
      { label: "Todos los mensajes", value: "all" },
      { label: "Mis mensajes", value: "own" },
      { ...savedOption },
    ],
    [savedOption]
  );
  const categoryOptions: Array<SelectOption> = useMemo(
    () => [
      { label: "Filtrar por categoría", value: "all" },
      { label: "About SQY", value: "secuoyas" },
      { label: "Estados de ánimo", value: "moods" },
      { label: "Recomendaciones", value: "recommendations" },
      { label: "Recursos", value: "resources" },
      { label: "Virtual coffees", value: "coffees" },
    ],
    []
  );
  const [moodsFilter, setMoodsFilter] = useState<SelectOption>(
    filterOptions[0]
  );
  const [categoryFilter, setCategoryFilter] = useState<SelectOption>(
    categoryOptions[0]
  );
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const isSavedFilter = moodsFilter?.value === "saved";

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isLoadMoreVisible = useViewportSpy(
    loadMoreRef as unknown as MutableRefObject<HTMLElement>
  );
  const [showLoadMore, setShowLoadMore] = useState(true);
  const loadMoreMoods = showLoadMore && !!isLoadMoreVisible;
  const lastItemTimestamp = moods?.[moods.length - 1]?.created._seconds;

  const updateMoodData = (moodUpdated: MoodState) => {
    setMoods(
      (state) =>
        state?.map((mood) =>
          mood.id === moodUpdated.id ? { ...mood, ...moodUpdated } : mood
        )
    );
  };

  const getMoods = useCallback(() => {
    if (isSavedFilter) {
      remoteApi.getMoodsById(savedMoods).then((moods: Array<MoodState>) => {
        setMoods(moods || []);
        setShowLoadMore(false);
      });
    } else {
      remoteApi
        .getMoods(
          moodsFilter?.value as "all" | "hidden" | "own",
          (categoryFilter?.value as string) || "all",
          {
            itemsPerPage,
            lastItemTimestamp,
          }
        )
        .then((moods: Array<MoodState>) => {
          if (moods?.length) {
            setMoods((state = []) => [...state, ...moods]);
            if (moods.length < itemsPerPage) {
              setShowLoadMore(false);
            }
          } else {
            setMoods((state = []) => [...state]);
            setShowLoadMore(false);
          }
        });
    }
  }, [
    lastItemTimestamp,
    moodsFilter?.value,
    savedMoods,
    isSavedFilter,
    categoryFilter?.value,
  ]);

  const resetMoods = () => {
    setMoods(undefined);
    setShowLoadMore(true);
  };

  const getStats = () =>
    remoteApi.getMoodStats().then((stats) => setStats(stats));

  useEffect(() => {
    getStats();
  }, []);

  useEffect(() => {
    if (!loadMoreMoods || !loadMoreRef.current) return;
    const rect = loadMoreRef.current.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.top <= window.innerHeight;
    if (!isVisible) return;
    getMoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreMoods]);

  useEffect(() => {
    resetMoods();
  }, [moodsFilter?.value, categoryFilter?.value]);

  useEffect(() => {
    if (isSavedFilter) {
      setMoodsFilter(savedOption);
    }
  }, [savedOption, isSavedFilter]);

  return (
    <VStack gap="s" minHeight="100vh">
      {/* Charts */}
      <Columns hs="l" vs="l">
        <Column width={{ default: "100%", l: "70%", xl: "35%" }}>
          <DayChart data={stats?.todayMoods} />
        </Column>
        <Column width={{ default: "100%", l: "30%", xl: "230px" }}>
          <AverageCard average={stats?.averageToday} users={stats?.userCount} />
        </Column>
        <Column width={{ default: "100%", xl: "45%" }}>
          <AverageChart data={stats?.averageMoods} setStats={setStats} />
        </Column>
      </Columns>

      {/* Timeline */}
      <div ref={timelineRef}>
        <VStack gap="m" mt="xs">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex>
              <Box maxWidth={240}>
                <Select
                  value={moodsFilter}
                  options={filterOptions}
                  onChange={setMoodsFilter}
                  isClearable={false}
                />
              </Box>
              <Box maxWidth={240} ml="s">
                <Select
                  value={categoryFilter}
                  options={categoryOptions}
                  onChange={setCategoryFilter}
                  isClearable={false}
                />
              </Box>
            </Flex>
            <Button
              appearance="secondary"
              action={() => setIsCommentModalOpen(true)}
              leftIcon="edit"
            >
              Dejar un comentario
            </Button>
          </Flex>

          <Timeline
            moods={moods}
            resetMoods={resetMoods}
            updateMoodData={updateMoodData}
            getStats={getStats}
            timelineRef={timelineRef}
            newMoods={newMoods}
            setNewMoods={setNewMoods}
          />
        </VStack>
      </div>

      {/* Add new comment modal */}
      <Modal
        isOpen={isCommentModalOpen}
        hide={() => setIsCommentModalOpen(false)}
        title="Dejar un comentario"
        size="S"
        height="auto"
      >
        <MoodWidget
          onSubmit={() => {
            resetMoods();
            setNewMoods(0);
            setIsCommentModalOpen(false);
          }}
          modalStyle
        />
      </Modal>

      {/* loading more spinner */}
      <Box ref={loadMoreRef}>
        <Flex alignItems="center" justifyContent="center">
          {showLoadMore && <Loader />}
        </Flex>
      </Box>
    </VStack>
  );
};

export default Mood;
